import { ReactNode, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { useMuiModal } from '../../../_core/hooks';
import { ConfirmationModal, ConfirmationModalProps } from '../../../_core/components/confirmation-modal';
import { EngagementDto } from '@families-link/shared';
import { buttonModalWindowStyles } from '../../../_core/styles';
import { Theme } from '@mui/material';

export type useOpenConfirmModalFormHookProps = {
	featchAction(values: EngagementDto | EngagementDto[]): Promise<void>;
	handlePagination?(): void;
	title: string;
	description?: string;
	icon?: ReactNode;
	danger?: boolean;
	info?: boolean;
	submitButtonName?: string;
	toastText?: string;
	customBackgrondColorKey?: keyof Theme['colors'];
	customColorKey?: keyof Theme['colors'];
	customBorderColorKey?: keyof Theme['colors'];
};

export function useOpenConfirmModal(props: useOpenConfirmModalFormHookProps) {
	const {
		featchAction,
		handlePagination,
		title,
		description,
		icon,
		danger = false,
		info = false,
		submitButtonName,
		toastText,
		customBackgrondColorKey,
		customBorderColorKey,
		customColorKey,
	} = props;
	const { t } = useTranslation();
	const { enqueueSnackbar } = useSnackbar();

	const showToast = (message: string, variant: 'error' | 'default' | 'success' | 'warning' | 'info' | undefined) => {
		enqueueSnackbar(message, {
			variant,
		});
	};

	const { openModal, closeModal } = useMuiModal<ConfirmationModalProps>(ConfirmationModal);
	const handleConfirm = async (value: EngagementDto | EngagementDto[]): Promise<void> => {
		try {
			await featchAction(value);
			showToast(toastText || 'Successfully performed', 'success');
			handlePagination && handlePagination();
			closeModal();
		} catch (error) {
			console.error(error);
		}
	};

	const handleOpenConfirmModal = useCallback(
		(value: EngagementDto | EngagementDto[]) => {
			const handleSubmit = async () => {
				await handleConfirm(value);
			};

			let engagementTitles: string;
			if (Array.isArray(value)) {
				engagementTitles = value
					.map((val) => {
						const titleField = val.customFields?.find((field) => field.name === 'Title');
						return val?.customFieldsValues?.find((e) => e.fieldId.toString() === titleField?._id.toString())?.value;
					})
					.join(', ');
			} else {
				const titleField = value.customFields?.find((field) => field.name === 'Title');
				engagementTitles =
					value?.customFieldsValues?.find((e) => e.fieldId.toString() === titleField?._id.toString())?.value || '';
			}

			openModal({
				title,
				description: description?.replace(':engagementTitle', engagementTitles),
				onSubmit: handleSubmit,
				onClose: closeModal,
				icon,
				customBackgrondColorKey: customBackgrondColorKey,
				customColorKey: customColorKey,
				customBorderColorKey: customBorderColorKey,
				danger,
				leftButtonProps: {
					children: t('engagements.forms.buttons.cancel'),
					variant: 'secondary',
					sx: buttonModalWindowStyles,
				},
				rightButtonProps: {
					children: submitButtonName,
					variant: danger ? 'danger' : info ? 'info' : 'primary',
					contraModeStyleButton: true,
					sx: buttonModalWindowStyles,
				},
			});
		},
		[handleConfirm, openModal]
	);

	return handleOpenConfirmModal;
}
